import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../i18n';

const DOMAIN = 'https://planiaro.com';

// Pages that should NEVER be indexed (blacklist)
const blockedPatterns = [
  /^\/([a-z]{2})?\/login(\/|$)/i,
  /^\/([a-z]{2})?\/register(\/|$)/i,
  /^\/([a-z]{2})?\/dashboard(\/|$)/i,
  /^\/([a-z]{2})?\/viajes(\/|$)/i,
  /^\/([a-z]{2})?\/admin(\/|$)/i,
  /^\/([a-z]{2})?\/api(\/|$)/i,
  /^\/([a-z]{2})?\/itinerario(\/|$)/i,  // Result page, no indexar
  /^\/([a-z]{2})?\/forgot-password(\/|$)/i,
  /^\/([a-z]{2})?\/reset-password(\/|$)/i,
  /^\/([a-z]{2})?\/verify-email(\/|$)/i,
  /^\/([a-z]{2})?\/baja(\/|$)/i,
  /^\/([a-z]{2})?\/contact(\/|$)/i,
  /^\/([a-z]{2})?\/privacy-policy(\/|$)/i,  // Página legal, sin valor SEO — noindex (title/desc idénticos entre idiomas causaban "Duplicada: Google eligió otra canónica")
];

function isAllowed(path) {
  return !blockedPatterns.some((re) => re.test(path));
}

function extractLanguage(pathname) {
  const match = pathname.match(/^\/([a-z]{2})/i);
  return match && SUPPORTED_LANGUAGES.includes(match[1].toLowerCase())
    ? match[1].toLowerCase()
    : DEFAULT_LANGUAGE;
}

function getPathWithoutLang(pathname) {
  // Only strip the lang prefix when it is a complete path segment (/xx/ or /xx end-of-string)
  // Without the (\/ | $) guard, /planificador-ia would match /pl and become /anificador-ia,
  // causing hreflang tags like /elanificador-ia/, /deanificador-ia/, etc.
  const match = pathname.match(/^\/([a-z]{2})(\/|$)/i);
  if (match && SUPPORTED_LANGUAGES.includes(match[1].toLowerCase())) {
    return pathname.slice(3) || '/';
  }
  return pathname;
}

function createOrUpdateMeta(name, content) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function createOrUpdateLink(rel, hreflang, href) {
  // Only manage non-Helmet links; Helmet-managed links have data-react-helmet attr
  let link = document.querySelector(`link[rel="${rel}"][hreflang="${hreflang}"]:not([data-react-helmet])`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    link.setAttribute('hreflang', hreflang);
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

/**
 * Remove all non-Helmet hreflang links from the document head.
 */
function removeNonHelmetHreflang() {
  document.querySelectorAll('link[rel="alternate"][hreflang]:not([data-react-helmet])').forEach(el => el.remove());
}

export default function SeoMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    const isIndexable = isAllowed(pathname);
    const robotsContent = isIndexable ? 'index,follow' : 'noindex,follow';
    createOrUpdateMeta('robots', robotsContent);

    // Update html lang attribute
    const lang = extractLanguage(pathname);
    document.documentElement.lang = lang;

    // Canonical URL
    const pathWithoutLang = getPathWithoutLang(pathname);
    const normalizedPath = pathWithoutLang === '/' ? '/' : pathWithoutLang.replace(/\/$/, '');

    // Build canonical: /{lang}/ for homepage, /{lang}/path/ for subpages (ALWAYS trailing slash)
    let canonicalPath = normalizedPath === '/'
      ? `/${lang}/`
      : `/${lang}${normalizedPath}/`;
    // Safety: collapse any accidental double slashes
    canonicalPath = canonicalPath.replace(/\/+/g, '/');
    const canonicalUrl = `${DOMAIN}${canonicalPath}`;

    // Only set canonical if react-helmet hasn't already placed one
    const helmetCanonical = document.querySelector('link[rel="canonical"][data-react-helmet="true"]');
    if (!helmetCanonical) {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalUrl);
    } else {
      document.querySelectorAll('link[rel="canonical"]:not([data-react-helmet])').forEach(el => el.remove());
    }

    // Hreflang: yield to Helmet if a per-page component already manages them.
    // BlogPost.jsx emits hreflang via <Helmet>; we must not duplicate.
    const helmetHreflang = document.querySelector('link[rel="alternate"][hreflang][data-react-helmet="true"]');

    if (isIndexable && !helmetHreflang) {
      // No page-level Helmet hreflang — emit generic hreflang via DOM
      SUPPORTED_LANGUAGES.forEach((langCode) => {
        const hrefUrl = normalizedPath === '/'
          ? `${DOMAIN}/${langCode}/`
          : `${DOMAIN}/${langCode}${normalizedPath}/`;
        createOrUpdateLink('alternate', langCode, hrefUrl);
      });

      // x-default: ALWAYS point to /en/ version (international fallback).
      // NEVER use bare "/" which triggers a client-side redirect to /es/.
      // Google reports that as "Redirect page" in Search Console.
      const xDefaultUrl = normalizedPath === '/'
        ? `${DOMAIN}/en/`
        : `${DOMAIN}/en${normalizedPath}/`;
      createOrUpdateLink('alternate', 'x-default', xDefaultUrl);
    } else if (helmetHreflang) {
      // Helmet manages hreflang — remove our non-Helmet ones
      removeNonHelmetHreflang();
    } else {
      // Non-indexable page: clean up
      removeNonHelmetHreflang();
    }
  }, [pathname]);

  return null;
}
